<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Positionen</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('positions')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        v-if="isAdmin"
                        aria-label="Position anlegen"
                        class="btn-stage-icon-plain"
                        :to="positionNewPath"
                        title="Position anlegen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getDocumentations"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto sm:overflow-hidden">
                        <template v-if="documentationList.length > 0">
                            <div
                                class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border"
                                :class="{ 'position-list-hide-created-by': !showCreatedByColumn }"
                            >
                                <div ref="positionListHeader" class="stage-listview-split-header position-list-split-header" @scroll.passive="syncPositionHeaderScroll">
                                    <table class="stage-listview-split-table position-list-split-table table-listview table-listview--compact-y">
                                        <colgroup>
                                            <col class="w-[3.25rem]">
                                            <col class="position-list-col-title">
                                            <col class="w-[10rem]">
                                            <col class="w-[18rem]">
                                            <col class="w-[16rem]">
                                            <col v-if="showCreatedByColumn" class="w-[13rem]">
                                            <col class="w-[12rem]">
                                            <col class="w-28">
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th
                                                class="table-listview-l-th w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 whitespace-nowrap text-center border-r border-ultralight"
                                                style="overflow: visible; text-overflow: clip;"
                                            >
                                                ID
                                            </th>
                                            <th class="table-listview-l-th position-list-col-title" style="overflow: visible; text-overflow: clip;">
                                                Titel
                                            </th>
                                            <th class="table-listview-l-th w-[10rem]" style="overflow: visible; text-overflow: clip;">
                                                Kostenfreigaben
                                            </th>
                                            <th class="table-listview-l-th w-[18rem]" style="overflow: visible; text-overflow: clip;">
                                                Status
                                            </th>
                                            <th class="table-listview-l-th w-[16rem]" style="overflow: visible; text-overflow: clip;">
                                                Unternehmen
                                            </th>
                                            <th v-if="showCreatedByColumn" class="table-listview-l-th w-[13rem]" style="overflow: visible; text-overflow: clip;">
                                                Erstellt von
                                            </th>
                                            <th class="table-listview-l-th w-[12rem]" style="overflow: visible; text-overflow: clip;">
                                                Letztes Update
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-right w-28 min-w-[7rem]" style="overflow: visible; text-overflow: clip;">
                                                {{ $t('Actions') }}
                                            </th>
                                        </tr>
                                        </thead>
                                    </table>
                                </div>
                                <div ref="positionListBody" class="stage-listview-split-body position-list-split-body" @scroll.passive="syncPositionBodyScroll">
                                    <table class="stage-listview-split-table position-list-split-table table-listview table-listview--zebra table-listview--compact-y">
                                        <colgroup>
                                            <col class="w-[3.25rem]">
                                            <col class="position-list-col-title">
                                            <col class="w-[10rem]">
                                            <col class="w-[18rem]">
                                            <col class="w-[16rem]">
                                            <col v-if="showCreatedByColumn" class="w-[13rem]">
                                            <col class="w-[12rem]">
                                            <col class="w-28">
                                        </colgroup>
                                        <tbody class="bg-white">
                                        <template v-for="documentation in documentationList" :key="'position-row-' + documentation.id">
                                            <tr
                                                class="cursor-pointer"
                                                @click="$router.push(positionManagePath(documentation.id))"
                                            >
                                                <td
                                                    class="table-listview-l-td align-middle whitespace-no-wrap w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 py-0 text-center border-r border-ultralight"
                                                    style="background-color: var(--table-listview-zebra-color);"
                                                >
                                                    <div class="flex items-center justify-center h-full min-h-[4.5rem] px-1">
                                                        <span class="text-xs leading-4 text-gray-400 font-medium">{{ documentation.id }}</span>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap position-list-col-title position-list-title-cell">
                                                    <span
                                                        v-if="positionPinningAvailable && documentation.is_pinned"
                                                        class="position-pin-marker"
                                                        title="Angepinnt"
                                                    >
                                                        <svg-vue class="position-pin-marker-icon" icon="font-awesome.regular.thumbtack"></svg-vue>
                                                    </span>
                                                    <div :class="['position-title-cell-content', positionPinningAvailable && documentation.is_pinned ? 'position-title-cell-content--pinned' : '']">
                                                        <div v-if="getPositionProjects(documentation).length > 0" class="flex items-center flex-shrink-0">
                                                            <template v-for="(project, projectIndex) in getPositionProjects(documentation)" :key="'position-project-' + documentation.id + '-' + project.id">
                                                                <img
                                                                   
                                                                    :src="positionProjectImageUrl(project)"
                                                                    :alt="project.title || 'Projektbild'"
                                                                    :title="positionProjectTooltip(project)"
                                                                    :class="projectIndex === 0 ? '' : '-ml-7'"
                                                                    class="h-9 w-9 rounded-sm object-cover border border-white"
                                                                >
                                                            </template>
                                                        </div>
                                                        <div :class="getPositionProjects(documentation).length > 0 ? 'ml-3' : ''" class="min-w-0 flex-1">
                                                            <div class="text-sm font-medium text-gray-700 truncate">
                                                                {{ documentation.title }}
                                                            </div>
                                                            <div class="text-sm text-gray-500 truncate max-w-xl">
                                                                {{ getBodyPreview(documentation.body_excerpt) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[10rem]">
                                                    <div class="flex items-center justify-center">
                                                        <span
                                                            :aria-label="costApprovalTooltipText(documentation)"
                                                            :title="costApprovalTooltipText(documentation)"
                                                            class="inline-block h-3 w-3 rounded-full"
                                                            :class="costApprovalDotClass(documentation)"
                                                        ></span>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[18rem]">
                                                    <div class="text-sm text-gray-700 font-medium inline-flex items-center">
                                                        <span>{{ getCurrentProcessStepLabel(documentation) }}</span>
                                                        <app-tooltip
                                                            v-if="hasProcessStepsTooltip(documentation)"
                                                            class="ml-2"
                                                            width-class="w-max max-w-[22rem]"
                                                            placement="bottom"
                                                        >
                                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.circle-info"></svg-vue>
                                                            <template #content>
                                                                <div class="px-3 py-2">
                                                                    <div
                                                                        v-for="(processStep, processStepIndex) in getProcessStepsForTooltip(documentation)"
                                                                        :key="'position-process-step-tooltip-' + documentation.id + '-' + processStep.id"
                                                                        class="position-process-step-tooltip-item"
                                                                    >
                                                                        <span
                                                                            class="position-process-step-tooltip-dot"
                                                                            style="width: 10px; height: 10px;"
                                                                            :style="{ backgroundColor: getProcessStepDotColor(documentation, processStep, processStepIndex) }"
                                                                        ></span>
                                                                        <span
                                                                            :title="processStep.name"
                                                                            class="position-process-step-tooltip-text"
                                                                        >
                                                                            {{ processStep.name }}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </template>
                                                        </app-tooltip>
                                                    </div>
                                                    <div class="mt-1 w-72 max-w-full">
                                                        <div class="h-1.5 bg-gray-300 rounded-full overflow-hidden">
                                                            <div
                                                                class="h-full rounded-full"
                                                                :style="{ width: getProgressWidth(documentation) + '%', backgroundColor: getCurrentProcessStepColor(documentation) }"
                                                            ></div>
                                                        </div>
                                                        <div class="mt-1 text-xs text-gray-500">
                                                            {{ getDisplayProcessStepProgressDone(documentation) }} / {{ documentation.process_steps_count || 0 }}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[16rem]">
                                                    <div
                                                        v-if="documentation.company"
                                                        class="flex items-center min-w-0"
                                                    >
                                                        <img
                                                            :src="getUserAvatar(documentation.company)"
                                                            :alt="documentation.company.name || 'Unternehmen'"
                                                            class="h-9 w-9 rounded-sm mr-3 object-cover"
                                                            @error="handleAvatarImageError($event, documentation.company)"
                                                        >
                                                        <div class="text-sm text-gray-700 truncate">
                                                            {{ documentation.company.name || '-' }}
                                                        </div>
                                                    </div>
                                                    <div
                                                        v-else
                                                        class="text-sm text-gray-500"
                                                    >
                                                        keine Zuweisung
                                                    </div>
                                                </td>
                                                <td v-if="showCreatedByColumn" class="table-listview-l-td whitespace-no-wrap w-[13rem]">
                                                    <div class="flex items-center min-w-0">
                                                        <img
                                                            :src="getUserAvatar(documentation.user)"
                                                            :alt="documentation.user ? documentation.user.name : 'Avatar'"
                                                            class="h-8 w-8 rounded-full mr-3"
                                                            @error="handleAvatarImageError($event, documentation.user)"
                                                        >
                                                        <div class="text-sm text-gray-700 truncate">
                                                            {{ documentation.user ? documentation.user.name : '-' }}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[12rem]">
                                                    <div class="text-sm text-gray-500">
                                                        {{ $momentFormatDateTimeAgo(documentation.updated_at) }}
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td w-28 min-w-[7rem] text-right relative" @click.stop>
                                                    <div class="inline-flex items-center justify-end w-full relative">
                                                        <button
                                                            type="button"
                                                            class="inline-flex items-center justify-center h-7 w-7 text-gray-500"
                                                            @click.stop.prevent="toggleRowActions(documentation.id)"
                                                        >
                                                            <span class="flex flex-col items-center justify-center space-y-0.5">
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                            </span>
                                                        </button>
                                                        <div
                                                            v-if="activeActionsPositionId === documentation.id"
                                                            class="absolute right-0 top-9 z-30 w-56 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-sm"
                                                        >
                                                            <button
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="openPosition(documentation)"
                                                            >
                                                                {{ $t('Open position') }}
                                                            </button>
                                                            <button
                                                                v-if="positionPinningAvailable"
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="togglePositionPin(documentation)"
                                                            >
                                                                {{ documentation.is_pinned ? 'Position lösen' : 'Position anpinnen' }}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="openPositionInNewWindow(documentation)"
                                                            >
                                                                {{ $t('Open in new window') }}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="openCreatorPositions(documentation)"
                                                            >
                                                                {{ $t('Positions of sender') }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="!loading">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filterSidebarOpen"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row">
                                <button
                                    class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                    type="button"
                                    @click="changeSort"
                                >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="getDocumentations"
                                    >
                                        <option value="title">Titel</option>
                                        <option v-if="showCreatedByColumn" value="user_id">Erstellt von</option>
                                        <option value="current_process_step_id">Status</option>
                                        <option value="updated_at">Letztes Update</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>
                            <div class="stage-filter-label mt-4">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm transition ease-in-out duration-150"
                                        @change="getDocumentations"
                                    >
                                </div>
                            </div>
                            <template v-if="showEntriesFilter">
                                <div class="stage-filter-label mt-5">{{ $t('Entries') }}</div>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="filters.entries"
                                        :aria-label="$t('Entries').toString()"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="changeEntriesFilter"
                                    >
                                        <option value="assigned_process_steps">{{ $t('With process step assignment') }}</option>
                                        <option value="all">{{ $t('All positions') }}</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </template>
                            <template v-if="canFilterCompanies">
                                <div class="stage-filter-label mt-5">{{ $t('Companies') }}</div>
                                <input-select
                                    id="position-list-company-filter"
                                    v-model="companyFilterValue"
                                    class="stage-filter-select"
                                    :options="companyFilterOptions"
                                    option-label="name"
                                    @change="changeCompanyFilter"
                                >
                                    <template v-slot:selectedOption="props">
                                        <template v-if="!props || !props.anySelected">
                                            <span class="block truncate">{{ $t('All companies') }}</span>
                                        </template>
                                        <template v-else>
                                            <span class="block truncate">{{ props.option ? props.option.name : $t('All companies') }}</span>
                                        </template>
                                    </template>
                                    <template v-slot:selectOption="props">
                                        <div class="flex items-center space-x-3">
                                            <div v-if="props.option && Number(props.option.id) > 0 && props.option.avatarUrl" class="flex-shrink-0 inline-block">
                                                <img
                                                    :src="props.option.avatarUrl"
                                                    alt="Avatar"
                                                    class="w-5 h-5 rounded-sm object-cover"
                                                    @error="handleAvatarImageError($event, props.option)"
                                                >
                                            </div>
                                            <div
                                                class="block truncate"
                                                :class="Number(props.option.id) === Number(companyFilterValue) ? 'font-semibold' : 'font-normal'"
                                            >
                                                {{ props.option.name }}
                                            </div>
                                        </div>
                                    </template>
                                </input-select>
                            </template>
                            <div class="stage-filter-label mt-5">{{ $t('Projects') }}</div>
                            <input-select
                                id="position-list-project-filter"
                                v-model="filters.projects"
                                class="stage-filter-select"
                                :options="projectList"
                                multiple
                                option-label="title"
                                @change="changeProjectFilter"
                            >
                                <template v-slot:selectOption="props">
                                    <div class="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            class="form-checkbox h-4 w-4 text-gray-600 pointer-events-none"
                                            :checked="Object.values(filters.projects).indexOf(props.option.id) > -1"
                                        >
                                        <div class="block truncate">
                                            {{ props.option.title }}
                                        </div>
                                    </div>
                                </template>
                            </input-select>
                            <div class="stage-filter-label mt-5">{{ $t('Items per page') }}</div>
                            <input-select
                                id="position-list-per-page-filter"
                                v-model="perPage"
                                class="stage-filter-select"
                                :options="[{id: 10, name: 10}, {id: 25, name: 25}, {id: 50, name: 50}, {id: 100, name: 100}]"
                                option-label="name"
                                @change="changePerPage"
                            />
                            <div v-if="hasActiveFilters" class="col-span-3 mt-4">
                                <button
                                    id="positions-filter-reset-btn"
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <list-footer-pagination
                    :start="(pagination.perPage * pagination.currentPage) - pagination.perPage + 1"
                    :end="pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {resolveAccessState} from "@/utils/access-state";
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryHasKey,
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "position-list",
    metaInfo() {
        return {
            title: "Positionen"
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const initialProjectFilter = queryNumberArray(
            routeQuery,
            queryHasKey(routeQuery, 'list_projects') ? 'list_projects' : ['projects[]', 'projects'],
            []
        );
        const initialEntriesFilterRaw = queryString(
            routeQuery,
            queryHasKey(routeQuery, 'list_entries') ? 'list_entries' : 'entries',
            ''
        ).trim();
        const initialEntriesFilter = ['all', 'assigned_process_steps'].includes(initialEntriesFilterRaw)
            ? initialEntriesFilterRaw
            : null;
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 25, [10, 25, 50, 100]);
        const initialCompanyId = queryNumber(routeQuery, ['list_company_id', 'company_id'], null);
        const initialRelationshipUserId = queryNumber(routeQuery, 'list_relationship_user_id', null);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        if (!['title', 'user_id', 'current_process_step_id', 'updated_at'].includes(initialSortColumn)) {
            initialSortColumn = 'updated_at';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'desc';
        }

        return {
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            filters: {
                search: queryString(routeQuery, 'list_search', null),
                company_id: initialCompanyId !== null && initialCompanyId > 0 ? initialCompanyId : null,
                projects: initialProjectFilter,
                entries: initialEntriesFilter,
                relationship_user_id: initialRelationshipUserId !== null && initialRelationshipUserId > 0
                    ? initialRelationshipUserId
                    : null,
            },
            sort: {
                order: initialSortOrder,
                column: initialSortColumn,
            },
            perPage: initialPerPage !== null ? initialPerPage : 25,
            loading: true,
            pagination: {
                currentPage: 1,
                perPage: 25,
                total: 0,
                totalPages: 1,
            },
            filterSidebarOpen: false,
            activeActionsPositionId: null,
            documentationList: [],
            positionPinningAvailable: false,
            companies: [],
            projectList: [],
            canFilterCompanies: false,
            isHydratingFilters: false,
            isSyncingPositionListScroll: false,
        }
    },
    mounted() {
        if (!this.canUseRelationshipFilter) {
            this.filters.relationship_user_id = null;
        }
        this.ensureEntriesFilterDefault();
        this.getDocumentations();
        document.addEventListener('click', this.handleDocumentClick);
        this.resetPositionListHorizontalScroll();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        accessState() {
            return resolveAccessState(this.$store);
        },
        canUseRelationshipFilter() {
            return !!this.accessState.accessContext?.modules?.relationships;
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isEmployeeLike() {
            return this.accessState.isEmployeeLike;
        },
        isServiceProvider() {
            return this.accessState.isServiceProvider;
        },
        showCreatedByColumn() {
            return !this.isServiceProvider;
        },
        showEntriesFilter() {
            return this.isEmployeeLike && !this.isAdmin;
        },
        defaultEntriesFilter() {
            return this.showEntriesFilter ? 'assigned_process_steps' : 'all';
        },
        isCustomerPositionRoute() {
            return String(this.$route.path || '').startsWith('/positions');
        },
        positionsListPath() {
            return this.isCustomerPositionRoute ? '/positions/list' : '/workspace/positions';
        },
        positionNewPath() {
            return this.isCustomerPositionRoute ? '/positions/new' : '/workspace/positions/new';
        },
        companyFilterValue: {
            get() {
                return this.filters.company_id ? Number(this.filters.company_id) : 0;
            },
            set(value) {
                const companyId = Number(value || 0);
                this.filters.company_id = companyId > 0 ? companyId : null;
            },
        },
        companyFilterOptions() {
            const options = (this.companies || []).map((company) => ({
                id: Number(company.id),
                name: company.name || '-',
                avatarUrl: this.getUserAvatar(company),
                gravatar: company && company.gravatar ? company.gravatar : '',
            }));

            return [{id: 0, name: this.$t('All companies').toString(), avatarUrl: ''}, ...options];
        },
        hasActiveFilters() {
            const hasSearch = (this.filters.search || '').trim() !== '';
            const hasCompany = Number(this.filters.company_id || 0) > 0;
            const hasProjects = Array.isArray(this.filters.projects) && this.filters.projects.length > 0;
            const hasRelationshipUser = this.canUseRelationshipFilter
                && Number(this.filters.relationship_user_id || 0) > 0;
            const hasSortChange = this.sort.order !== 'desc' || this.sort.column !== 'updated_at';
            const hasPerPageChange = Number(this.perPage || 0) !== 25;
            const hasEntriesFilter = this.showEntriesFilter
                && this.normalizeEntriesFilter(this.filters.entries) !== this.defaultEntriesFilter;

            return hasSearch || hasCompany || hasProjects || hasRelationshipUser || hasSortChange || hasPerPageChange || hasEntriesFilter;
        },
    },
    methods: {
        normalizeEntriesFilter(value) {
            const normalizedValue = String(value || '').trim().toLowerCase();
            if (['all', 'assigned_process_steps'].includes(normalizedValue)) {
                return normalizedValue;
            }
            return this.defaultEntriesFilter;
        },
        ensureEntriesFilterDefault() {
            const normalizedValue = this.normalizeEntriesFilter(this.filters.entries);
            if (this.filters.entries !== normalizedValue) {
                this.filters.entries = normalizedValue;
            }
        },
        positionManagePath(positionId) {
            return this.isCustomerPositionRoute
                ? routeWithReturnTo('/positions/' + positionId, this.$route, this.positionsListPath)
                : routeWithReturnTo('/workspace/positions/' + positionId + '/manage', this.$route, this.positionsListPath);
        },
        resetPositionListHorizontalScroll() {
            const header = this.$refs.positionListHeader;
            const body = this.$refs.positionListBody;

            if (header) {
                header.scrollLeft = 0;
            }

            if (body) {
                body.scrollLeft = 0;
            }
        },
        handleDocumentClick() {
            this.activeActionsPositionId = null;
        },
        syncPositionHeaderScroll() {
            if (this.isSyncingPositionListScroll) {
                return;
            }

            const header = this.$refs.positionListHeader;
            const body = this.$refs.positionListBody;

            if (!header || !body) {
                return;
            }

            this.isSyncingPositionListScroll = true;
            body.scrollLeft = header.scrollLeft;
            requestAnimationFrame(() => {
                this.isSyncingPositionListScroll = false;
            });
        },
        syncPositionBodyScroll() {
            if (this.isSyncingPositionListScroll) {
                return;
            }

            const header = this.$refs.positionListHeader;
            const body = this.$refs.positionListBody;

            if (!header || !body) {
                return;
            }

            this.isSyncingPositionListScroll = true;
            header.scrollLeft = body.scrollLeft;
            requestAnimationFrame(() => {
                this.isSyncingPositionListScroll = false;
            });
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getDocumentations();
            }
        },
        changeSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.getDocumentations();
        },
        changeCompanyFilter() {
            const normalizedId = Number(this.filters.company_id);
            this.filters.company_id = normalizedId > 0 ? normalizedId : null;
            this.page = 1;
            this.getDocumentations();
        },
        changeProjectFilter() {
            if (this.isHydratingFilters) {
                return;
            }
            this.page = 1;
            this.getDocumentations();
        },
        changeEntriesFilter() {
            this.filters.entries = this.normalizeEntriesFilter(this.filters.entries);
            this.page = 1;
            this.getDocumentations();
        },
        changePerPage() {
            this.page = 1;
            this.getDocumentations();
        },
        resetFilters() {
            this.filters.search = null;
            this.filters.company_id = null;
            this.filters.projects = [];
            this.filters.entries = this.defaultEntriesFilter;
            this.filters.relationship_user_id = null;
            this.sort.order = 'desc';
            this.sort.column = 'updated_at';
            this.perPage = 25;
            this.page = 1;
            this.getDocumentations();
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        toggleRowActions(positionId) {
            this.activeActionsPositionId = this.activeActionsPositionId === positionId ? null : positionId;
        },
        openPosition(position) {
            this.activeActionsPositionId = null;
            this.$router.push(this.positionManagePath(position.id));
        },
        openPositionInNewWindow(position) {
            this.activeActionsPositionId = null;
            window.open(this.positionManagePath(position.id), '_blank');
        },
        togglePositionPin(position) {
            if (!this.positionPinningAvailable) {
                this.activeActionsPositionId = null;
                return;
            }

            const self = this;
            const wasPinned = Boolean(position?.is_pinned);
            const request = wasPinned
                ? axios.delete('api/workspace/positions/' + position.id + '/pin')
                : axios.post('api/workspace/positions/' + position.id + '/pin');

            request.then(function (response) {
                position.is_pinned = !wasPinned;
                position.pinned_at = response.data?.pinned_at || null;
                self.activeActionsPositionId = null;
                self.getDocumentations();
                if (self.$notify) {
                    self.$notify({
                        type: 'success',
                        text: self.$t('Data updated correctly'),
                    });
                }
            });
        },
        openCreatorPositions(position) {
            this.activeActionsPositionId = null;
            const creatorName = position && position.user && position.user.name ? position.user.name : '';
            window.open(this.positionsListPath + '?user=' + encodeURIComponent(creatorName), '_blank');
        },
        syncPositionListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_company_id',
                'list_projects',
                'list_entries',
                'list_relationship_user_id',
                'company_id',
                'projects',
                'projects[]',
                'entries',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 25), 25);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'updated_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            setQueryValue(nextQuery, 'list_company_id', Number(this.filters.company_id || 0) > 0 ? Number(this.filters.company_id) : null);
            setQueryValue(nextQuery, 'list_projects', this.filters.projects, []);
            setQueryValue(nextQuery, 'list_entries', this.normalizeEntriesFilter(this.filters.entries), this.defaultEntriesFilter);
            setQueryValue(
                nextQuery,
                'list_relationship_user_id',
                this.canUseRelationshipFilter && Number(this.filters.relationship_user_id || 0) > 0
                    ? Number(this.filters.relationship_user_id)
                    : null
            );

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            const candidates = [
                user.avatar_url,
                user.avatar,
                user.gravatar,
                user.image,
                user.picture,
            ];

            for (let i = 0; i < candidates.length; i += 1) {
                const value = candidates[i];
                if (typeof value === 'string' && value.trim() !== '' && value !== 'gravatar') {
                    return value;
                }
            }

            return '';
        },
        handleAvatarImageError(event, user) {
            if (!event || !event.target) {
                return;
            }

            const fallback = user && typeof user.gravatar === 'string' ? user.gravatar : '';
            if (fallback && event.target.src !== fallback) {
                event.target.src = fallback;
                return;
            }

            event.target.style.display = 'none';
        },
        getProgressWidth(position) {
            const total = Number(position.process_steps_count || 0);
            if (total < 1) {
                return 0;
            }

            const done = this.getDisplayProcessStepProgressDone(position);
            return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
        },
        getDisplayProcessStepProgressDone(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length < 1) {
                return 0;
            }

            const completedCount = Number(position?.completed_process_steps_count || 0);
            return Math.max(0, Math.min(completedCount, processSteps.length));
        },
        getCurrentProcessStepColor(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length < 1) {
                return UI_COLORS.text.default;
            }

            const completedCount = this.getDisplayProcessStepProgressDone(position);
            if (completedCount >= processSteps.length) {
                const lastStep = processSteps[processSteps.length - 1];
                return (lastStep && lastStep.color) ? lastStep.color : UI_COLORS.text.default;
            }

            if (position && position.current_process_step && position.current_process_step.color) {
                return position.current_process_step.color;
            }

            if (completedCount > 0) {
                const lastCompletedIndex = Math.min(completedCount - 1, processSteps.length - 1);
                const lastCompletedStep = processSteps[lastCompletedIndex];
                return (lastCompletedStep && lastCompletedStep.color) ? lastCompletedStep.color : UI_COLORS.text.default;
            }

            return UI_COLORS.text.default;
        },
        getCurrentProcessStepLabel(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length > 0) {
                const firstIncompleteStep = processSteps.find((step) => step && step.is_completed === false);
                if (firstIncompleteStep && firstIncompleteStep.name) {
                    return firstIncompleteStep.name;
                }

                const lastStep = processSteps[processSteps.length - 1];
                if (lastStep && lastStep.name) {
                    return lastStep.name;
                }
            }

            if (position && position.current_process_step && position.current_process_step.name) {
                return position.current_process_step.name;
            }

            return processSteps.length > 0 ? 'Offen' : '-';
        },
        getProcessStepsForTooltip(position) {
            if (!position || !Array.isArray(position.process_steps)) {
                return [];
            }

            return position.process_steps
                .filter((step) => step && step.id && step.name)
                .sort((a, b) => Number(a.value || 0) - Number(b.value || 0));
        },
        hasProcessStepsTooltip(position) {
            return this.getProcessStepsForTooltip(position).length > 0;
        },
        getProcessStepDotColor(position, processStep, processStepIndex) {
            const currentStepId = Number(position?.current_process_step?.id || 0);
            if (currentStepId > 0 && Number(processStep?.id || 0) === currentStepId) {
                return processStep.color || UI_COLORS.text.subtle;
            }

            if (typeof processStep?.is_completed === 'boolean') {
                return processStep.is_completed ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
            }

            const completedCount = this.getDisplayProcessStepProgressDone(position);
            return processStepIndex < completedCount ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
        },
        normalizeCostApprovalStatus(position) {
            const status = String(position?.customer_cost_approval_status || '').trim().toLowerCase();
            if (['approved', 'adjusted', 'pending', 'none'].includes(status)) {
                return status;
            }
            return 'none';
        },
        costApprovalDotClass(position) {
            const status = this.normalizeCostApprovalStatus(position);
            if (status === 'approved') {
                return 'bg-green-500';
            }
            if (status === 'adjusted') {
                return 'bg-orange-400';
            }
            if (status === 'pending') {
                return 'bg-gray-400';
            }
            return 'bg-blue-500';
        },
        costApprovalTooltipText(position) {
            const status = this.normalizeCostApprovalStatus(position);
            if (status === 'approved') {
                return 'Alle Aufwände Freigegeben';
            }
            if (status === 'adjusted') {
                return 'Aufwände teilweise Freigegeben';
            }
            if (status === 'pending') {
                return 'Kostenfreigabe ausstehend';
            }
            return 'Keine freigabepflichtigen Aufwände';
        },
        getDocumentations() {
            const self = this;
            self.loading = true;
            self.syncPositionListRouteState();
            axios.get(self.isCustomerPositionRoute ? 'api/positions' : 'api/workspace/positions', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    company_ids: self.filters.company_id ? [self.filters.company_id] : [],
                    projects: self.filters.projects,
                    entries: self.normalizeEntriesFilter(self.filters.entries),
                    relationship_user_id: self.canUseRelationshipFilter
                        ? self.filters.relationship_user_id
                        : null,
                }
            }).then(function (response) {
                self.documentationList = response.data.items;
                self.positionPinningAvailable = Boolean(response.data.position_pinning_available);
                const responsePagination = response.data.pagination || {};
                const normalizedCurrentPage = Number(responsePagination.currentPage || self.page || 1);
                const normalizedPerPage = Number(responsePagination.perPage || self.perPage || 25);
                const normalizedTotal = Number(responsePagination.total || 0);
                const normalizedTotalPages = Math.max(1, Number(responsePagination.totalPages || 1));

                self.pagination = {
                    currentPage: normalizedCurrentPage > 0 ? normalizedCurrentPage : 1,
                    perPage: normalizedPerPage > 0 ? normalizedPerPage : 25,
                    total: normalizedTotal >= 0 ? normalizedTotal : 0,
                    totalPages: normalizedTotalPages,
                };
                self.canFilterCompanies = !!response.data.can_filter_companies;
                self.companies = Array.isArray(response.data.companies)
                    ? response.data.companies
                    : [];
                self.projectList = Array.isArray(response.data.projects)
                    ? response.data.projects
                    : ((response.data.projects && Array.isArray(response.data.projects.data)) ? response.data.projects.data : []);
                self.isHydratingFilters = true;
                if (self.canFilterCompanies) {
                    const selectedIds = Array.isArray(response.data.selected_company_ids)
                        ? response.data.selected_company_ids
                        : [];
                    const normalizedIds = selectedIds
                        .map((id) => Number(id))
                        .filter((id) => id > 0);
                    const nextCompanyId = normalizedIds.length > 0 ? normalizedIds[0] : null;
                    if (Number(self.filters.company_id || 0) !== Number(nextCompanyId || 0)) {
                        self.filters.company_id = nextCompanyId;
                    }
                } else if (self.filters.company_id !== null) {
                    self.filters.company_id = null;
                }
                const nextProjectFilterIds = Array.isArray(response.data.selected_project_ids)
                    ? response.data.selected_project_ids.map((id) => Number(id)).filter((id) => id > 0)
                    : [];
                if (!self.sameIdArray(self.filters.projects, nextProjectFilterIds)) {
                    self.filters.projects = nextProjectFilterIds;
                }
                const nextEntriesFilter = self.normalizeEntriesFilter(response.data.selected_entries);
                if (self.filters.entries !== nextEntriesFilter) {
                    self.filters.entries = nextEntriesFilter;
                }
                const nextRelationshipUserId = self.canUseRelationshipFilter
                    ? Number(response.data.selected_relationship_user_id || 0)
                    : 0;
                if (Number(self.filters.relationship_user_id || 0) !== nextRelationshipUserId) {
                    self.filters.relationship_user_id = nextRelationshipUserId > 0 ? nextRelationshipUserId : null;
                }
                self.isHydratingFilters = false;
                self.syncPositionListRouteState();
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getDocumentations();
                } else {
                    self.page = self.pagination.currentPage;
                    self.loading = false;
                    self.$nextTick(() => {
                        self.resetPositionListHorizontalScroll();
                    });
                }
            }).catch(function () {
                self.isHydratingFilters = false;
                self.loading = false;
            });
        },
        sameIdArray(current, next) {
            const left = Array.isArray(current) ? current.map((id) => Number(id)).filter((id) => id > 0) : [];
            const right = Array.isArray(next) ? next.map((id) => Number(id)).filter((id) => id > 0) : [];
            if (left.length !== right.length) {
                return false;
            }

            for (let index = 0; index < left.length; index += 1) {
                if (left[index] !== right[index]) {
                    return false;
                }
            }

            return true;
        },
        getBodyPreview(value) {
            if (!value) {
                return '-';
            }

            const stripped = String(value)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!stripped) {
                return '-';
            }

            const decoder = document.createElement('textarea');
            decoder.innerHTML = stripped;

            return (decoder.value || decoder.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim() || '-';
        },
        getPositionProjects(position) {
            const projects = Array.isArray(position?.projects)
                ? position.projects
                : (position?.projects && Array.isArray(position.projects.data) ? position.projects.data : []);
            if (projects.length > 0) {
                return projects
                    .filter((project) => project && Number(project.id) > 0);
            }

            if (position && position.project && Number(position.project.id) > 0) {
                return [position.project];
            }

            return [];
        },
        positionProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        positionProjectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== '' ? `Projekt: ${title}` : 'Projekt';
        },
    }
}
</script>

<style scoped>
.position-list-split-header {
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.position-list-split-header::-webkit-scrollbar {
    display: none;
}

.position-list-split-body {
    overflow-x: auto;
}

.position-list-split-table {
    min-width: 98rem;
}

.position-list-col-title {
    width: calc(100% - 81.25rem);
    min-width: 300px;
}

.position-list-hide-created-by .position-list-col-title {
    width: calc(100% - 68.25rem);
}

.position-pin-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #dc2626;
    color: #ffffff;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2rem;
}

.position-pin-marker-icon {
    width: 0.9375rem;
    height: 0.9375rem;
    color: #ffffff;
}

.table-listview-l-td.position-list-title-cell {
    padding: 0 !important;
    position: relative;
    overflow: hidden;
}

.position-title-cell-content {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    padding: 0.25rem 1.25rem;
    min-height: 4.5rem;
}

.position-title-cell-content--pinned {
    padding-left: 3.25rem;
}

.position-process-step-tooltip-item {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 0;
}

.position-process-step-tooltip-item:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 15px;
    bottom: -8px;
    width: 1px;
    background: var(--ui-border-default);
    z-index: 1;
}

.position-process-step-tooltip-dot {
    position: relative;
    z-index: 2;
    margin-right: 9px;
    flex-shrink: 0;
    border-radius: 9999px;
}

.position-process-step-tooltip-text {
    display: block;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 16px;
    color: var(--ui-text-secondary);
}

</style>
